import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, ChevronUp, Package, Clock, Truck, CheckCircle, XCircle, UtensilsCrossed, Bell, Star, RefreshCw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { formatCurrency } from '@/lib/utils';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import OrderRatingDialog from './OrderRatingDialog';
import { notifyOrderStatus, getNotificationPermission, requestNotificationPermission } from '@/lib/pushNotifications';

const NEGATIVE_STATUSES = ['rejected', 'cancelled', 'failed'];

// Compact status labels
const STATUS_LABELS = {
  placed: 'Order Placed',
  accepted: 'Accepted',
  preparing: 'Preparing',
  ready: 'Ready',
  out_for_delivery: 'On the Way',
  delivered: 'Delivered',
  completed: 'Completed',
  rejected: 'Rejected',
  cancelled: 'Cancelled'
};

const OrderStatusTracker = ({ customerPhone, isVisible = true }) => {
  const [activeOrders, setActiveOrders] = useState([]);
  const [isMinimized, setIsMinimized] = useState(false);
  const [showTracker, setShowTracker] = useState(false);
  const [previousStatuses, setPreviousStatuses] = useState({});
  const [showRatingDialog, setShowRatingDialog] = useState(false);
  const [orderToRate, setOrderToRate] = useState(null);
  const [notificationPermission, setNotificationPermission] = useState('default');
  const pollIntervalRef = useRef(null);

  useEffect(() => {
    setNotificationPermission(getNotificationPermission());
  }, []);

  // Fetch active orders
  const fetchOrders = useCallback(async () => {
    if (!customerPhone) return;
    
    try {
      const response = await api.get(`/orders/customer/${customerPhone}`);
      const orders = response.data;
      
      const now = new Date();
      
      // Filter orders: only show TRULY active orders (not completed/delivered unless very recent)
      const activeOnes = orders.filter(order => {
        const updatedAt = new Date(order.updated_at || order.created_at);
        const minutesSinceUpdate = (now - updatedAt) / (1000 * 60);
        const hoursSinceUpdate = minutesSinceUpdate / 60;
        
        // Rejected/cancelled: show for 5 minutes only
        if (NEGATIVE_STATUSES.includes(order.status)) {
          return minutesSinceUpdate < 5;
        }
        
        // Delivered/completed: DON'T show in tracker (only show rating prompt)
        if (['delivered', 'completed'].includes(order.status)) {
          return false;
        }
        
        // Active orders: show
        return true;
      });
      
      // Find orders that need rating (delivered in last 24 hours, not yet rated)
      const ordersToRate = orders.filter(order => {
        if (order.rating) return false; // Already rated
        if (!['delivered', 'completed'].includes(order.status)) return false;
        
        const updatedAt = new Date(order.updated_at || order.created_at);
        const hoursSinceUpdate = (now - updatedAt) / (1000 * 60 * 60);
        return hoursSinceUpdate < 24; // Within 24 hours
      });
      
      // Check for status changes and trigger notifications
      activeOnes.forEach(order => {
        const prevStatus = previousStatuses[order.id];
        if (prevStatus && prevStatus !== order.status) {
          triggerNotification(order, prevStatus, order.status);
        }
      });
      
      // Update previous statuses
      const newPrevStatuses = {};
      [...activeOnes, ...ordersToRate].forEach(order => {
        newPrevStatuses[order.id] = order.status;
      });
      setPreviousStatuses(newPrevStatuses);
      
      setActiveOrders(activeOnes);
      
      // Auto-show tracker if there are active orders
      if (activeOnes.length > 0 && !isMinimized) {
        setShowTracker(true);
      } else if (activeOnes.length === 0) {
        setShowTracker(false);
      }
      
      // Auto-prompt rating for most recent unrated delivered order
      if (ordersToRate.length > 0 && !showRatingDialog && !orderToRate) {
        const mostRecent = ordersToRate[0];
        // Only auto-show if delivered in last 5 minutes
        const updatedAt = new Date(mostRecent.updated_at || mostRecent.created_at);
        const minutesSinceDelivery = (now - updatedAt) / (1000 * 60);
        if (minutesSinceDelivery < 5) {
          setOrderToRate(mostRecent);
          setShowRatingDialog(true);
        }
      }
      
    } catch (error) {
      console.error('Error fetching orders for tracker:', error);
    }
  }, [customerPhone, previousStatuses, isMinimized, showRatingDialog, orderToRate]);

  // Trigger notification for status change
  const triggerNotification = (order, oldStatus, newStatus) => {
    // Send browser push notification
    if (notificationPermission === 'granted') {
      notifyOrderStatus(newStatus, order.order_number, order.id);
    }
    
    // Play sound for important status changes
    if (['rejected', 'cancelled'].includes(newStatus)) {
      playNotificationSound('error');
    } else if (['delivered', 'completed', 'ready'].includes(newStatus)) {
      playNotificationSound('success');
    }
  };

  const playNotificationSound = (type) => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const frequencies = type === 'error' ? [392, 330] : [523, 659, 784];
      
      frequencies.forEach((freq, index) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        oscillator.frequency.value = freq;
        oscillator.type = 'sine';
        const startTime = audioContext.currentTime + (index * 0.15);
        oscillator.start(startTime);
        oscillator.stop(startTime + 0.15);
        gainNode.gain.setValueAtTime(0.3, startTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + 0.15);
      });
    } catch (e) {
      console.log('Sound play failed:', e);
    }
  };

  // Poll for order updates
  useEffect(() => {
    if (customerPhone) {
      fetchOrders();
      pollIntervalRef.current = setInterval(fetchOrders, 5000);
    }
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [customerPhone, fetchOrders]);

  const handleRequestPermission = async () => {
    const result = await requestNotificationPermission();
    setNotificationPermission(result.permission);
  };

  // Don't render if no active orders and not minimized with orders
  if (!isVisible || (activeOrders.length === 0 && !isMinimized)) {
    return (
      <>
        {/* Rating Dialog - can appear even without active orders */}
        <OrderRatingDialog
          order={orderToRate}
          isOpen={showRatingDialog}
          onClose={() => {
            setShowRatingDialog(false);
            setOrderToRate(null);
          }}
          onRatingSubmitted={() => {
            fetchOrders();
          }}
        />
      </>
    );
  }

  const currentOrder = activeOrders[0];
  const isNegativeStatus = currentOrder && NEGATIVE_STATUSES.includes(currentOrder.status);

  return (
    <>
      {/* Minimized Floating Button - to restore tracker */}
      {isMinimized && activeOrders.length > 0 && (
        <motion.button
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          onClick={() => {
            setIsMinimized(false);
            setShowTracker(true);
          }}
          className="fixed bottom-4 right-4 z-50 bg-[#FF4500] text-white p-3 rounded-full shadow-lg flex items-center gap-2"
        >
          <Package className="w-5 h-5" />
          <span className="text-sm font-medium pr-1">{activeOrders.length}</span>
        </motion.button>
      )}

      {/* Compact Order Tracker */}
      <AnimatePresence>
        {showTracker && !isMinimized && currentOrder && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="fixed bottom-0 left-0 right-0 z-50"
          >
            <div className="bg-[#1a1a1a] border-t border-white/10 rounded-t-xl shadow-2xl mx-2 mb-0">
              {/* Compact Header */}
              <div className="flex items-center justify-between px-3 py-2">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  {/* Status Icon */}
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    isNegativeStatus ? 'bg-red-500' : 
                    currentOrder.status === 'preparing' ? 'bg-orange-500' :
                    currentOrder.status === 'ready' ? 'bg-green-500' :
                    currentOrder.status === 'out_for_delivery' ? 'bg-purple-500' :
                    'bg-blue-500'
                  }`}>
                    {isNegativeStatus ? <XCircle className="w-4 h-4 text-white" /> :
                     currentOrder.status === 'preparing' ? <UtensilsCrossed className="w-4 h-4 text-white" /> :
                     currentOrder.status === 'out_for_delivery' ? <Truck className="w-4 h-4 text-white" /> :
                     <Package className="w-4 h-4 text-white" />}
                  </div>
                  
                  {/* Order Info */}
                  <div className="min-w-0 flex-1">
                    <p className="text-white text-sm font-medium truncate">
                      {currentOrder.order_number}
                    </p>
                    <p className={`text-xs ${isNegativeStatus ? 'text-red-400' : 'text-green-400'}`}>
                      {STATUS_LABELS[currentOrder.status] || currentOrder.status}
                    </p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1">
                  {activeOrders.length > 1 && (
                    <span className="text-gray-400 text-xs mr-1">+{activeOrders.length - 1}</span>
                  )}
                  <button 
                    onClick={() => setIsMinimized(true)}
                    className="p-1.5 rounded-full hover:bg-white/10"
                  >
                    <X className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
              </div>

              {/* Rejected Order - Refund Info */}
              {isNegativeStatus && (
                <div className="px-3 pb-3">
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-2">
                    <p className="text-red-400 text-xs">
                      {currentOrder.status === 'rejected' ? 'Order was rejected by restaurant' : 'Order was cancelled'}
                    </p>
                    {currentOrder.refund_id ? (
                      <p className="text-green-400 text-xs mt-1">
                        ✓ Refund initiated ({formatCurrency(currentOrder.refund_amount || currentOrder.total_amount)}) - Will be credited to your account within 5-7 business days
                      </p>
                    ) : currentOrder.razorpay_payment_id ? (
                      <p className="text-yellow-400 text-xs mt-1">
                        Refund processing... Will be credited within 5-7 business days
                      </p>
                    ) : (
                      <p className="text-gray-400 text-xs mt-1">
                        COD order - No payment to refund
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Enable Notifications (only show once) */}
              {notificationPermission === 'default' && !isNegativeStatus && (
                <div className="px-3 pb-2">
                  <button
                    onClick={handleRequestPermission}
                    className="w-full bg-blue-500/20 text-blue-400 text-xs py-1.5 rounded-lg flex items-center justify-center gap-1"
                  >
                    <Bell className="w-3 h-3" />
                    Enable notifications for updates
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Order Rating Dialog */}
      <OrderRatingDialog
        order={orderToRate}
        isOpen={showRatingDialog}
        onClose={() => {
          setShowRatingDialog(false);
          setOrderToRate(null);
        }}
        onRatingSubmitted={() => {
          fetchOrders();
        }}
      />
    </>
  );
};

export default OrderStatusTracker;
